import { apiFetch } from "@/lib/auth-api";

/**
 * Device-agent auto-update kampanie (Phase 5). Backend posyła/odbiera camelCase;
 * ścieżki idą przez apiFetch, który dokleja `/api` + Bearer — podawaj je BEZ `/api`.
 */

export type UpdateComponent = "service" | "helper" | "rustdesk";

export type RolloutStrategy = "immediate" | "stagger" | "canary";

export type CampaignStatus = "draft" | "active" | "paused" | "completed";

export type CampaignStage = "canary" | "percent" | "full";

/** Per-device wynik aplikacji aktualizacji. */
export type UpdateResultStatus =
	| "pending"
	| "downloading"
	| "installing"
	| "succeeded"
	| "rolled_back"
	| "failed";

export interface Campaign {
	campaignId: number;
	tenantId: number | null;
	component: UpdateComponent;
	versionTo: string;
	artifactUrl: string;
	artifactSha256: string;
	/** Authenticode / Ed25519 — szkielet, kolumna nullable. */
	signature: string | null;
	targetFilter: Record<string, unknown>;
	rolloutStrategy: RolloutStrategy;
	canaryPercent: number;
	staggerWindowSec: number;
	status: CampaignStatus;
	currentStage: CampaignStage;
	createdByUserId: number | null;
	createdAt: string;
	updatedAt: string;
}

export interface CampaignResult {
	resultId: number;
	campaignId: number;
	deviceUuid: string;
	status: UpdateResultStatus;
	versionFrom: string | null;
	versionTo: string | null;
	commandId: number | null;
	startedAt: string | null;
	completedAt: string | null;
	rollbackReason: string | null;
	/** Już zredagowane po stronie backendu (redact_error_details). */
	errorDetails: string | null;
	createdAt: string;
	updatedAt: string;
	/**
	 * Termin kolejnej automatycznej próby (ta sama reguła co worker ponowień);
	 * `null`, gdy żadna nie jest zaplanowana.
	 */
	nextRetryAt: string | null;
	/** Worker ponowi też po wykryciu restartu komputera (faza dzienna). */
	retryOnReboot: boolean;
	/**
	 * Automat już nie ponowi: wyczerpany horyzont albo błąd trwały
	 * (np. `downgrade_blocked`, niezgodny SHA-256) — „Wymaga interwencji”.
	 */
	needsIntervention: boolean;
}

export interface CreateCampaignInput {
	component: UpdateComponent;
	versionTo: string;
	artifactUrl: string;
	artifactSha256: string;
	signature?: string | null;
	targetFilter?: Record<string, unknown>;
	rolloutStrategy?: RolloutStrategy;
	canaryPercent?: number;
	staggerWindowSec?: number;
	tenantId?: number | null;
}

export function listCampaigns(): Promise<Campaign[]> {
	return apiFetch<Campaign[]>("/device-agent/update-campaigns");
}

export function createCampaign(input: CreateCampaignInput): Promise<Campaign> {
	return apiFetch<Campaign>("/device-agent/update-campaigns", {
		method: "POST",
		body: JSON.stringify({
			component: input.component,
			versionTo: input.versionTo,
			artifactUrl: input.artifactUrl,
			artifactSha256: input.artifactSha256,
			signature: input.signature ?? null,
			targetFilter: input.targetFilter ?? {},
			rolloutStrategy: input.rolloutStrategy ?? "canary",
			canaryPercent: input.canaryPercent ?? 30,
			staggerWindowSec: input.staggerWindowSec ?? 3600,
			tenantId: input.tenantId ?? null,
		}),
	});
}

/** Aktywuj kampanię — przechodzi z draft do active (stage=canary). */
export function activateCampaign(campaignId: number): Promise<Campaign> {
	return apiFetch<Campaign>(`/device-agent/update-campaigns/${campaignId}/activate`, {
		method: "POST",
	});
}

/** Ręczny rollout: rozszerz canary -> percent -> full. */
export function advanceCampaign(campaignId: number, stage: CampaignStage): Promise<Campaign> {
	return apiFetch<Campaign>(`/device-agent/update-campaigns/${campaignId}/advance`, {
		method: "POST",
		body: JSON.stringify({ stage }),
	});
}

export function pauseCampaign(campaignId: number): Promise<Campaign> {
	return apiFetch<Campaign>(`/device-agent/update-campaigns/${campaignId}/pause`, {
		method: "POST",
	});
}

export function completeCampaign(campaignId: number): Promise<Campaign> {
	return apiFetch<Campaign>(`/device-agent/update-campaigns/${campaignId}/complete`, {
		method: "POST",
	});
}

export function listCampaignResults(campaignId: number): Promise<CampaignResult[]> {
	return apiFetch<CampaignResult[]>(`/device-agent/update-campaigns/${campaignId}/results`);
}

/**
 * Ręczny rollback urządzenia w ramach kampanii. Backend bierze wersję docelową
 * z device_update_results.version_from bieżącego wyniku.
 */
export function rollbackDevice(deviceUuid: string, campaignId: number): Promise<CampaignResult> {
	return apiFetch<CampaignResult>(
		`/device-agent/devices/${encodeURIComponent(deviceUuid)}/rollback`,
		{
			method: "POST",
			body: JSON.stringify({ campaignId }),
		},
	);
}

/**
 * Ręczne ponowienie nieudanej aktualizacji. Omija backoff/limit prób auto-retry
 * — operator decyduje (np. po naprawie sieci na stanowisku). Wymaga online.
 */
export function retryDevice(deviceUuid: string, campaignId: number): Promise<CampaignResult> {
	return apiFetch<CampaignResult>(`/device-agent/devices/${encodeURIComponent(deviceUuid)}/retry`, {
		method: "POST",
		body: JSON.stringify({ campaignId }),
	});
}
